import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Check } from "lucide-react";

const SPECIALTY_OPTIONS = [
  "Therapeutic Touch",
  "Trauma-Informed Care",
  "Weighted Immersion",
  "Reiki",
  "Grounding",
  "Somatic Practice",
  "Playful Presence",
];

const SESSION_TYPES = ["In-Person", "Home Visit", "Virtual"];

export default function CuddlerApply() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: user?.name || "",
    location: "",
    city: "",
    state: "",
    bio: "",
    hourly_rate: 95,
    years_experience: 1,
    gender: "",
    specialties: [],
    session_types: ["In-Person"],
    photo_path: null,
    verification_paths: [],
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [verifFiles, setVerifFiles] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/cuddlers/my-application").then((r) => setExisting(r.data.application));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-24 px-6 text-center" data-testid="apply-signin">
        <h1 className="font-serif text-4xl text-[#F7ECEB]">Sign in to apply.</h1>
        <Link to="/login" className="btn-primary inline-block mt-6">Sign in</Link>
      </div>
    );
  }

  if (existing) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6" data-testid="apply-status">
        <h1 className="font-serif text-4xl text-[#F7ECEB]">Your application</h1>
        <div className={`mt-8 tt-card p-8`}>
          <div className={`text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-block ${
            existing.status === "approved" ? "bg-[#C93B3B] text-white" :
            existing.status === "rejected" ? "bg-red-700 text-white" : "bg-[#110D0D] text-[#A89896]"
          }`}>{existing.status}</div>
          <h2 className="font-serif text-2xl mt-4 text-[#F7ECEB]">{existing.name}</h2>
          <p className="text-sm text-[#A89896] mt-2">{existing.location}</p>
          <p className="text-[#F7ECEB] mt-4 leading-relaxed">{existing.bio}</p>
          {existing.status === "pending" && (
            <p className="text-sm text-[#A89896] mt-6">
              Our team reviews every application by hand. You'll be notified as soon as we've made a decision. Thank you for your patience.
            </p>
          )}
          {existing.status === "approved" && (
            <Link to="/dashboard" className="btn-primary inline-block mt-6">Go to dashboard</Link>
          )}
          {existing.status === "rejected" && existing.rejection_reason && (
            <p className="text-sm text-red-700 mt-4">Note: {existing.rejection_reason}</p>
          )}
        </div>
      </div>
    );
  }

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.specialties.length === 0) { setErr("Please choose at least one specialty."); return; }
    if (!photoFile) { setErr("A profile photo is required."); return; }
    if (verifFiles.length === 0) { setErr("At least one verification document is required."); return; }
    setLoading(true);
    try {
      const photo_path = await upload(photoFile, "public");
      const verification_paths = [];
      for (const f of verifFiles) verification_paths.push(await upload(f, "private"));
      await api.post("/cuddlers/apply", {
        ...form,
        photo_path,
        verification_paths,
        hourly_rate: Number(form.hourly_rate),
        years_experience: Number(form.years_experience),
      });
      await refresh();
      nav("/apply");
      window.location.reload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Application failed");
    } finally { setLoading(false); }
  };

  const toggleSpec = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter((x) => x !== s)
        : [...f.specialties, s],
    }));
  };
  const toggleSession = (s) => {
    setForm((f) => ({
      ...f,
      session_types: f.session_types.includes(s)
        ? f.session_types.filter((x) => x !== s)
        : [...f.session_types, s],
    }));
  };

  return (
    <div className="max-w-3xl mx-auto py-16 px-6" data-testid="apply-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">Practitioner application</div>
      <h1 className="font-serif text-5xl mt-4 text-[#F7ECEB]">Become a Touche cuddle therapist.</h1>
      <p className="text-[#A89896] mt-6 leading-relaxed">
        We hand-review every application. You'll be asked for a professional headshot and at least one verification document (photo ID, certification, or training credential). All verification files are private and visible only to Touche administrators.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6 tt-card p-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Full name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Gender</label>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="apply-gender" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]">
              <option value="">Prefer not to say</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">City</label>
            <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, location: `${e.target.value}, ${form.state}` })} data-testid="apply-city" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">State</label>
            <input required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase(), location: `${form.city}, ${e.target.value.toUpperCase()}` })} data-testid="apply-state" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" placeholder="CA" />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">About you (bio)</label>
          <textarea required rows={5} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="apply-bio" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" placeholder="Share your approach, training, and what a session with you feels like." />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Specialties</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {SPECIALTY_OPTIONS.map((s) => (
              <button type="button" key={s} onClick={() => toggleSpec(s)}
                data-testid={`spec-${s.replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  form.specialties.includes(s)
                    ? "bg-[#C93B3B] text-white border-[#C93B3B]"
                    : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A] hover:border-[#C93B3B]"
                }`}>{form.specialties.includes(s) && <Check className="w-3 h-3 inline mr-1" />}{s}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Session types offered</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {SESSION_TYPES.map((s) => (
              <button type="button" key={s} onClick={() => toggleSession(s)}
                data-testid={`session-${s.replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm border ${
                  form.session_types.includes(s)
                    ? "bg-[#C93B3B] text-white border-[#C93B3B]"
                    : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A]"
                }`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Hourly rate (USD) · min $60</label>
            <input required type="number" min={60} value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="apply-rate" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
            <p className="text-xs text-[#A89896] mt-1">You keep 85% · Touche keeps 15%</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Years of experience</label>
            <input required type="number" min={0} value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} data-testid="apply-years" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Profile photo (public)</label>
          <label className="mt-2 flex items-center gap-3 border border-dashed border-[#382A2A] rounded-xl p-4 bg-[#1F1717] cursor-pointer">
            <Upload className="w-5 h-5 text-[#C93B3B]" />
            <span className="text-sm text-[#A89896]">{photoFile ? photoFile.name : "Choose a warm, professional headshot"}</span>
            <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0])} data-testid="apply-photo-input" className="hidden" />
          </label>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Verification documents (private, admin only)</label>
          <p className="text-xs text-[#A89896] mt-1">Upload a government-issued ID plus any relevant training or certification. Files are encrypted and only visible to Touche administrators.</p>
          <label className="mt-2 flex items-center gap-3 border border-dashed border-[#382A2A] rounded-xl p-4 bg-[#1F1717] cursor-pointer">
            <Upload className="w-5 h-5 text-[#C93B3B]" />
            <span className="text-sm text-[#A89896]">{verifFiles.length > 0 ? `${verifFiles.length} file(s) selected` : "Choose files (JPG, PNG, PDF)"}</span>
            <input type="file" accept="image/*,.pdf" multiple onChange={(e) => setVerifFiles(Array.from(e.target.files || []))} data-testid="apply-verif-input" className="hidden" />
          </label>
        </div>

        {err && <div className="text-sm text-red-700" data-testid="apply-error">{err}</div>}

        <button type="submit" disabled={loading} className="btn-primary" data-testid="apply-submit">
          {loading ? "Submitting..." : "Submit application"}
        </button>
      </form>
    </div>
  );
}
